import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Clock, X, ChevronDown } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicETAPage() {
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStop, setSelectedStop] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    fetchETAs();
    // Refresh every 30 seconds
    const interval = setInterval(fetchETAs, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchETAs = async () => {
    try {
      const response = await axios.get(`${API}/public/etas`);
      setStops(response.data.stops || []);
    } catch (err) {
      // Failed to fetch ETAs - will retry on next interval
    } finally {
      setLoading(false);
    }
  };

  const handleStopClick = (stop) => {
    setSelectedStop(stop);
    setDropdownOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/70">Loading delivery times...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex flex-col">
      {/* Main Content */}
      <main className="flex-1 p-6 flex flex-col items-center justify-center">
        {stops.length === 0 ? (
          <div className="text-center text-white/60">
            <MapPin className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>No active deliveries at the moment</p>
          </div>
        ) : (
          <div className="w-full max-w-md">
            {/* Dropdown selector */}
            <div className="relative mb-6">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full bg-white/10 hover:bg-white/15 border border-white/20 rounded-2xl p-4 flex items-center justify-between text-white transition-all"
                data-testid="stop-selector"
              >
                <span className="text-lg">
                  {selectedStop ? selectedStop.name : "Select your delivery..."}
                </span>
                <ChevronDown className={`w-5 h-5 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 right-0 mt-2 bg-slate-800 border border-white/20 rounded-2xl overflow-hidden shadow-2xl z-10"
                  >
                    {stops.map((stop, index) => (
                      <button
                        key={stop.id}
                        onClick={() => handleStopClick(stop)}
                        className="w-full p-4 text-left text-white hover:bg-white/10 transition-colors border-b border-white/10 last:border-b-0 flex items-center justify-between"
                        data-testid={`stop-option-${stop.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm">
                            {index + 1}
                          </span>
                          <span className="font-medium">{stop.name}</span>
                        </div>
                        <span className="text-white/60 text-sm">{stop.eta_text}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Prompt text */}
            {!selectedStop && (
              <p className="text-center text-white/50 text-sm">
                Tap above to select your name and see your delivery ETA
              </p>
            )}
          </div>
        )}
      </main>

      {/* ETA Modal */}
      <AnimatePresence>
        {selectedStop && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50"
            onClick={() => setSelectedStop(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl text-center"
              onClick={(e) => e.stopPropagation()}
              data-testid="eta-modal"
            >
              {/* Close button */}
              <button
                onClick={() => setSelectedStop(null)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                data-testid="close-modal-btn"
              >
                <X className="w-4 h-4 text-slate-600" />
              </button>

              {/* Icon */}
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-blue-600" />
              </div>

              {/* Name */}
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                {selectedStop.name}
              </h2>

              {/* ETA */}
              <div className="mb-6">
                <p className="text-slate-500 text-sm mb-1">Estimated arrival in</p>
                <p className="text-5xl font-bold text-blue-600">
                  {selectedStop.eta_text || "Calculating..."}
                </p>
              </div>

              {/* Thank you message */}
              <p className="text-slate-600 text-base">
                Thank you for your patience
              </p>

              {/* Tap to close hint */}
              <p className="text-slate-400 text-xs mt-6">
                Tap anywhere to close
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="p-4 text-center text-white/30 text-xs">
        ETAs update automatically
      </footer>
    </div>
  );
}
